import * as React from 'react';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import classNames from 'classnames';
import Typography from '@material-ui/core/Typography';
import { Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { fetchVerificationsIfNeeded } from 'actions/verifications';
import { IconListItem } from '@pec/aion-ui-components/components/IconListItem';
import { useAppDispatch, useTypedSelector } from '../../combineReducers';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Widget } from '../Widget';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {
      gap: `${theme.spacing(3)}px`,
      margin: `${theme.spacing(2)}px ${theme.spacing(1)}px 0`
    },
    verified: {
      backgroundColor: theme.palette.secondary.main
    },
    notVerified: {
      backgroundColor: theme.palette.error.main
    },
    inProgress: {
      backgroundColor: theme.palette.grey[400]
    },
    statusIndicator: {
      borderRadius: 2,
      height: 16,
      width: 16
    }
  })
);

type RouteParams = {
  organizationId: string;
};

export const Verifications: React.FC = () => {
  const { organizationId } = useParams<RouteParams>();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { verifications, isFetching, error } = useTypedSelector(state => state.verifications);
  const { t } = useTranslation('subscriber');

  React.useEffect(() => {
    if (organizationId) {
      dispatch(fetchVerificationsIfNeeded(organizationId));
    }
  }, [organizationId, dispatch]);

  return (
    <Widget
      isFetching={isFetching}
      hasFetchingError={!!error}
      title={t('newDashboard.verifications.title', 'Verifications')}
    >
      <Box display="flex" flexDirection="column" className={classes.list}>
        {verifications?.map(({ legacyId, name, status, isVerified }) => (
          <Box key={legacyId}>
            <Box>
              <Typography variant="body1">{name}</Typography>
            </Box>
            <Box display="flex" gridGap={8} alignItems="center">
              <Box
                className={classNames(classes.statusIndicator, {
                  [classes.verified]: isVerified,
                  [classes.notVerified]: isVerified === false,
                  [classes.inProgress]: isVerified === null
                })}
              />
              <Box>
                <Typography variant="body2">{status}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
        {!verifications || verifications.length === 0 ? (
          <IconListItem
            href={`/organizations/${organizationId}/questionnaire-v2`}
            icon={<ArrowForwardIosIcon />}
            primaryText={t('newDashboard.verifications.noVerifications', 'No Verifications are Available')}
          >
            {t(
              'newDashboard.verifications.completeSections',
              'Please complete the appropriate sections of your questionnaire.'
            )}
          </IconListItem>
        ) : null}
      </Box>
    </Widget>
  );
};
